import React, { useState, useEffect } from 'react';
import './App.css';
import { Routes, Route, useLocation } from 'react-router-dom';
import Header from './components/Header';
import Tabs from './components/Tabs';
import Home from './components/Home';
import SubmitInitiative from './components/SubmitInitiative';
import InitiativeManagement from './components/InitiativeManagement';
import ScreeningDashboard from './components/ScreeningDashboard';
import MySubmissions from './components/MySubmissions';
import BrowseInitiatives from './components/BrowseInitiatives';
import PartnershipRequests from './components/PartnershipRequests';
import DelegateManagement from './components/DelegateManagement';
import DQReviewerManagement from './components/DQReviewerManagement';
import ReviewerManagement from './components/ReviewerManagement';
import EmailApproval from './components/EmailApproval';
import ApprovalStatistics from './components/ApprovalStatistics';
import DataQualityReview from './components/DataQualityReview';
import ManageDivisions from './components/ManageDivisions';
import DivisionPrompt from './components/DivisionPrompt';
import AIDebugLog from './components/AIDebugLog';
import { useUser } from './contexts/UserContext';
import { useEditInitiative } from './contexts/EditInitiativeContext';
import axios from 'axios';
import { api } from './config';
import { fetchDivisions } from './utils/departments';
import { initResizableTables } from './utils/resizableTables';
// import { useBackendKeepAlive } from './hooks/useBackendKeepAlive';

function App() {
  const { user, isLoading, error, loadingMessage, isReviewer } = useUser();
  const { setOnSwitchTab } = useEditInitiative();
  const [activeTab, setActiveTab] = useState('home');
  const location = useLocation();

  // Seed division cache from DB on mount
  useEffect(() => { fetchDivisions(); }, []);
  
  // Initialize resizable tables
  useEffect(() => {
    const timers = [
      setTimeout(() => initResizableTables(), 100),
      setTimeout(() => initResizableTables(), 300),
      setTimeout(() => initResizableTables(), 600),
    ];
    return () => timers.forEach(t => clearTimeout(t));
  }, [activeTab, location.pathname]);
  
  // Keep backend alive while user is on the page - DISABLED
  // useBackendKeepAlive(300); // Ping every 5 minutes
  
  console.log('App.tsx - User:', user, 'isReviewer:', isReviewer);

  // Register the tab switch handler
  useEffect(() => {
    setOnSwitchTab(setActiveTab);
  }, [setOnSwitchTab]);

  if (error) {
    return (
      <div className="app" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', backgroundColor: '#f8f9fa' }}>
        <div style={{ textAlign: 'center', maxWidth: '500px', padding: '40px' }}>
          <div style={{ fontSize: '64px', marginBottom: '20px' }}>❌</div>
          <div style={{ fontSize: '20px', fontWeight: '600', marginBottom: '15px', color: '#dc3545' }}>
            Authentication Failed
          </div>
          <div style={{ fontSize: '14px', color: '#666', marginBottom: '20px', lineHeight: '1.6' }}>
            {error}
          </div>
          <div style={{ fontSize: '12px', color: '#999', marginTop: '30px', paddingTop: '20px', borderTop: '1px solid #ddd' }}>
            If the problem persists, please clear your browser cache and try again, or contact your system administrator.
          </div>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="app" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh' }}>
        <div style={{
          width: '48px',
          height: '48px',
          borderRadius: '50%',
          border: '4px solid #e0e0e0',
          borderTopColor: '#007bff',
          animation: 'spin 0.8s linear infinite'
        }}></div>
        <style>{`
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  const handleTabChange = (tabId: string) => {
    setActiveTab(tabId);
  };

  const tabs = [
    {
      id: 'home',
      label: 'Home',
      content: <Home />,
    },
    {
      id: 'submit',
      label: 'Submit an AI Initiative',
      content: <SubmitInitiative />,
    },
    {
      id: 'my-submissions',
      label: 'My Submissions',
      content: <MySubmissions />,
    },
    {
      id: 'partnership-requests',
      label: 'Partnership Requests',
      content: <PartnershipRequests />,
    },
    {
      id: 'browse-initiatives',
      label: 'Browse Initiatives',
      content: <BrowseInitiatives />,
    },
    isReviewer && {
      id: 'initiative-management',
      label: 'Review Initiatives',
      content: <InitiativeManagement />,
      section: 'approvers',
      sectionLabel: 'Approvers Only',
    },
    isReviewer && {
      id: 'data-quality-review',
      label: 'Data Quality Review',
      content: <DataQualityReview />,
      section: 'approvers',
    },
    isReviewer && {
      id: 'reviewer-management',
      label: 'Manage Reviewers',
      content: <ReviewerManagement />,
      section: 'approvers',
    },
    isReviewer && {
      id: 'delegate-management',
      label: 'Manage Delegates',
      content: <DelegateManagement />,
      section: 'approvers',
    },
    isReviewer && {
      id: 'dq-reviewer-management',
      label: 'Manage DQ Reviewers',
      content: <DQReviewerManagement />,
      section: 'approvers',
    },
    isReviewer && {
      id: 'statistics',
      label: 'Approval Statistics',
      content: <ApprovalStatistics />,
      section: 'approvers',
    },
    isReviewer && {
      id: 'manage-divisions',
      label: 'Manage Divisions',
      content: <ManageDivisions />,
      section: 'approvers',
    },
    isReviewer && {
      id: 'ai-check',
      label: 'AI Check',
      content: <AIDebugLog />,
      section: 'approvers',
    },
  ].filter(Boolean);

  return (
    <Routes>
      <Route
        path="/approve"
        element={<EmailApproval />}
      />
      <Route
        path="*"
        element={
          <div className="app">
            <Header title="MODP AI Solution Prescreener" />
            <DivisionPrompt />
            <div className="app-body">
              <div className="app-content">
                <Tabs tabs={tabs} defaultTab={activeTab} onTabChange={handleTabChange} />
              </div>
            </div>
          </div>
        }
      />
    </Routes>
  );
}

export default App;
